import React, { useEffect, useMemo, useState } from 'react';
import { Box, Divider, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
    MONO_FONT,
    INNER_RADIUS,
    MetricMatrix,
    PanelFrame,
    SectionFrame,
    StatusBadge,
    formatTimestamp,
    useDensity,
} from '../components/SharedFrames';
import StrategyEventFeedPanel from '../components/StrategyEventFeedPanel';
import useDhruvaState from './useDhruvaState';
import {
    CODE_LABELS,
    accountView,
    buildControlMetrics,
    buildHistory,
    codeTone,
    decisionLabel,
    decisionTone,
    inr,
    listAccounts,
    protectionNarrative,
    stopDescription,
} from './dhruvaView';

const ACCOUNT_KEY = 'qcockpit:dhruvaAccount';

const timeOnly = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
        timeZone: 'Asia/Kolkata' });
};

const Narrative = ({ label, text, tone = 'default' }) => {
    if (!text) return null;
    return (
        <Box
            sx={(theme) => {
                const color = tone === 'default' ? theme.palette.text.secondary
                    : theme.palette[tone === 'warning' ? 'warning' : tone === 'error' ? 'error' : 'success'].main;
                return {
                    borderLeft: `3px solid ${alpha(color, 0.7)}`,
                    pl: 1.1,
                    py: 0.3,
                };
            }}
        >
            <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
                color: 'text.secondary' }}>
                {label}
            </Typography>
            <Typography variant="body2" sx={{ lineHeight: 1.55 }}>{text}</Typography>
        </Box>
    );
};

const GateSparkline = ({ history = [] }) => {
    const theme = useTheme();
    const rows = history.filter((r) => r.ratio != null).slice(-30);
    if (!rows.length) return null;
    const W = 300;
    const H = 64;
    const max = Math.max(1.2, ...rows.map((r) => r.ratio), ...rows.map((r) => r.threshold || 0));
    const bw = W / rows.length;
    const y = (v) => H - (v / max) * (H - 4);
    const thrPath = rows
        .map((r, i) => (r.threshold != null ? `${i === 0 ? 'M' : 'L'}${(i + 0.5) * bw},${y(r.threshold)}` : ''))
        .join(' ');
    return (
        <Box>
            <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} role="img"
                aria-label="Gate ratio for the last 30 trading days against the skip threshold">
                {rows.map((r, i) => {
                    const skip = r.threshold != null && r.ratio > r.threshold;
                    return (
                        <rect key={r.tradingDay} x={i * bw + 1} y={y(r.ratio)} width={Math.max(1, bw - 2)}
                            height={H - y(r.ratio)}
                            fill={skip ? theme.palette.warning.main : theme.palette.success.main}
                            opacity={i === rows.length - 1 ? 1 : 0.55}>
                            <title>{`${r.tradingDay}: ratio ${r.ratio.toFixed(2)}${r.threshold != null ? ` vs ${r.threshold.toFixed(2)}` : ''} — ${skip ? 'skip' : 'trade'}`}</title>
                        </rect>
                    );
                })}
                <path d={thrPath} stroke={theme.palette.text.primary} strokeWidth="1.2" strokeDasharray="3 2" fill="none" />
            </svg>
            <Typography variant="caption" color="text.secondary">
                Last {rows.length} days: bars = opening-range ratio (green traded, amber skipped), dashed = threshold.
            </Typography>
        </Box>
    );
};

const DecisionPanel = ({ decision, gateHistory }) => {
    const d = decision || {};
    return (
        <PanelFrame
            accent="strategy"
            eyebrow="Today's Decision"
            title={d.decision ? `${d.decision}${d.tradingDay ? ` · ${d.tradingDay}` : ''}` : decisionLabel(null)}
            description="One decision at 09:39 for every account, from the 09:15–09:37 opening range and the ATM straddle."
            action={<StatusBadge label={decisionLabel(d.tradingDay ? d : null)} tone={decisionTone(d.decision)} />}
            minHeight={{ lg: 280 }}
        >
            <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                {d.narrative || 'The decision is made at 09:39 IST. Before then Dhruva runs its pre-flight checks and waits.'}
            </Typography>
            {d.ratio != null && d.threshold != null ? (
                <Box>
                    <Stack direction="row" justifyContent="space-between">
                        <Typography variant="caption" color="text.secondary">Opening move used</Typography>
                        <Typography variant="caption" sx={{ fontFamily: MONO_FONT }}>
                            {Number(d.ratio).toFixed(2)} of max {Number(d.threshold).toFixed(2)}
                        </Typography>
                    </Stack>
                    <Box sx={(theme) => ({ position: 'relative', height: 10, borderRadius: 5, mt: 0.5,
                        background: alpha(theme.palette.divider, 0.6) })}>
                        <Box sx={(theme) => ({
                            position: 'absolute', left: 0, top: 0, bottom: 0, borderRadius: 5,
                            width: `${Math.min(100, (d.ratio / Math.max(d.threshold * 1.6, d.ratio)) * 100)}%`,
                            background: d.ratio > d.threshold ? theme.palette.warning.main : theme.palette.success.main,
                        })} />
                        <Box sx={(theme) => ({
                            position: 'absolute', top: -3, bottom: -3, width: 2,
                            left: `${(d.threshold / Math.max(d.threshold * 1.6, d.ratio)) * 100}%`,
                            background: theme.palette.text.primary,
                        })} />
                    </Box>
                </Box>
            ) : null}
            <GateSparkline history={gateHistory} />
        </PanelFrame>
    );
};

const LegCard = ({ leg }) => {
    const { compact } = useDensity();
    const closed = leg.exitPrice != null || leg.status === 'closed';
    const pnl = leg.pnl ?? leg.pnlGross;
    const qty = leg.qty ?? leg.quantity;
    const cells = [
        ['Entry', inr(leg.entryPrice), leg.entryTime ? timeOnly(leg.entryTime) : null],
        [closed ? 'Exit' : 'LTP', inr(closed ? leg.exitPrice : leg.ltp), closed && leg.exitTime ? timeOnly(leg.exitTime) : null],
        ['Protection', stopDescription(leg), null],
        ['P&L (gross)', inr(pnl, 0), null],
    ];
    return (
        <Box sx={(theme) => ({
            borderRadius: INNER_RADIUS, border: '1px solid', borderColor: alpha(theme.palette.divider, 0.82),
            p: compact ? 1 : 1.3,
        })}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap spacing={1}>
                <Typography sx={{ fontWeight: 700, fontFamily: MONO_FONT }}>
                    {leg.strike} {leg.leg} · SELL {qty ?? '—'}
                </Typography>
                <StatusBadge
                    label={closed ? (CODE_LABELS[leg.exitCode] || 'Closed') : (leg.stopState ? leg.stopState.replace('_', ' ') : 'open')}
                    tone={closed ? codeTone(leg.exitCode) : leg.stopState === 'initial' ? 'default' : 'success'}
                />
            </Stack>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(4, minmax(0, 1fr))' }, gap: 1, my: 1 }}>
                {cells.map(([label, value, sub]) => (
                    <Box key={label}>
                        <Typography variant="caption" color="text.secondary">{label}</Typography>
                        <Typography variant="body2" sx={{ fontFamily: MONO_FONT, fontWeight: 600,
                            color: label.startsWith('P&L') ? (pnl > 0 ? 'success.main' : pnl < 0 ? 'error.main' : undefined) : undefined }}>
                            {value}
                        </Typography>
                        {sub ? <Typography variant="caption" color="text.secondary">{sub}</Typography> : null}
                    </Box>
                ))}
            </Box>
            <Stack spacing={1}>
                <Narrative label="Why it entered" text={leg.entryNarrative} tone="success" />
                {closed
                    ? <Narrative label="Why it exited" text={leg.exitNarrative} tone={codeTone(leg.exitCode)} />
                    : <Narrative label="Current protection" text={protectionNarrative(leg)} />}
            </Stack>
        </Box>
    );
};

const HistoryList = ({ items }) => (
    <Stack divider={<Divider flexItem />} spacing={1}>
        {items.map((item, i) => (
            <Stack key={`${item.at}-${item.code}-${i}`} direction="row" spacing={1.2} alignItems="flex-start">
                <Box sx={{ minWidth: 64, flexShrink: 0 }}>
                    <Typography variant="caption" sx={{ fontFamily: MONO_FONT }}>{timeOnly(item.at)}</Typography>
                </Box>
                <Box sx={{ minWidth: 150, flexShrink: 0 }}>
                    <StatusBadge label={`${item.leg ? `${item.leg} · ` : ''}${CODE_LABELS[item.code] || item.code}`}
                        tone={codeTone(item.code)} />
                </Box>
                <Typography variant="body2" sx={{ lineHeight: 1.55 }}>{item.text}</Typography>
            </Stack>
        ))}
    </Stack>
);

const DhruvaDesk = ({ executionMode }) => {
    const { data, error, fetchedAt } = useDhruvaState(true);
    const accounts = useMemo(() => listAccounts(data), [data]);
    const [account, setAccount] = useState(() => {
        try {
            return window.localStorage.getItem(ACCOUNT_KEY) || 'contra';
        } catch (_e) {
            return 'contra';
        }
    });
    useEffect(() => {
        if (accounts.length && !accounts.includes(account)) setAccount(accounts[0]);
    }, [accounts, account]);
    useEffect(() => {
        try { window.localStorage.setItem(ACCOUNT_KEY, account); } catch (_e) { /* per-viewer convenience only */ }
    }, [account]);

    const decision = data?.dhruva?.decision || null;
    const view = useMemo(() => accountView(data, account), [data, account]);
    const metrics = useMemo(() => buildControlMetrics({
        decision, summary: view.summary, legs: view.legs, day: view.day, capturedAt: view.capturedAt,
    }), [decision, view]);
    const history = useMemo(() => buildHistory(view.legs, view.dayEvents), [view]);
    const events = useMemo(() => (data?.events || [])
        .map((e) => ({ occurredAt: e.occurredAt, message: e.message })), [data]);
    const mode = view.summary.mode || view.day?.mode || (executionMode === 'live' ? 'live' : null);

    const skipped = decision?.decision && decision.decision !== 'TRADE';
    const emptyLegs = skipped
        ? decision.narrative
        : decision?.decision === 'TRADE'
            ? (view.day?.status && view.day.status !== 'open' && view.day.status !== 'preflight'
                ? `No position for ${account} today (${String(view.day.status).replace('_', ' ')}). See Position History for why.`
                : 'Entering the strangle…')
            : 'No position yet — the strangle is sold at 09:39 if the opening-range gate passes.';

    return (
        <SectionFrame
            accent="strategy"
            eyebrow="Strategy State"
            title="Dhruva Strategy Desk"
            description="BANKNIFTY short strangle sold at 09:39 on days that pass the opening-range gate. 2× exchange stops, the surviving leg moves to cost then trails, ₹6k/lot daily loss cap, square-off 15:10. Every entry and exit below says why it happened."
            status={(
                <Stack direction="row" spacing={0.9} flexWrap="wrap" useFlexGap>
                    <StatusBadge label={decisionLabel(decision)} tone={decisionTone(decision?.decision)} />
                    <StatusBadge label={mode ? String(mode).toUpperCase() : 'MODE ?'} tone={mode === 'live' ? 'success' : 'default'} />
                    <StatusBadge label={error ? 'API offline' : fetchedAt ? 'API live' : 'connecting'}
                        tone={error ? 'error' : fetchedAt ? 'success' : 'default'} />
                    {view.summary.manualLegs?.length ? (
                        <StatusBadge label={`MANUAL: ${view.summary.manualLegs.join(', ')}`} tone="error" />
                    ) : null}
                </Stack>
            )}
        >
            {accounts.length > 1 ? (
                <ToggleButtonGroup size="small" exclusive value={account}
                    onChange={(_e, v) => v && setAccount(v)} sx={{ mb: 1.5 }}>
                    {accounts.map((a) => <ToggleButton key={a} value={a}>{a}</ToggleButton>)}
                </ToggleButtonGroup>
            ) : null}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(12, minmax(0, 1fr))' }, gap: 2 }}>
                <Box sx={{ gridColumn: { xs: 'auto', lg: 'span 7' } }}>
                    <DecisionPanel decision={decision} gateHistory={data?.dhruva?.gateHistory || []} />
                </Box>
                <Box sx={{ gridColumn: { xs: 'auto', lg: 'span 5' } }}>
                    <PanelFrame
                        accent="strategy"
                        eyebrow="Operational Readout"
                        title="Control Metrics"
                        description={`Gate, premium, P&L against the loss cap, and the stop protecting each leg for ${account}. Updates every 2 seconds.`}
                        minHeight={{ lg: 280 }}
                    >
                        <MetricMatrix metrics={metrics}
                            columns={{ xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(2, minmax(0, 1fr))' }} />
                    </PanelFrame>
                </Box>
                <Box sx={{ gridColumn: { xs: 'auto', lg: 'span 7' } }}>
                    <PanelFrame
                        accent="strategy"
                        eyebrow="Leg Structure"
                        title="Live Legs"
                        description="Each leg with its prices, the stop currently protecting it, and the reason it was entered (and exited)."
                    >
                        {view.legs.length ? (
                            <Stack spacing={1.2}>
                                {view.legs.map((leg) => <LegCard key={`${leg.leg}-${leg.strike}`} leg={leg} />)}
                            </Stack>
                        ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>{emptyLegs}</Typography>
                        )}
                    </PanelFrame>
                </Box>
                <Box sx={{ gridColumn: { xs: 'auto', lg: 'span 5' } }}>
                    <PanelFrame
                        accent="strategy"
                        eyebrow="Position History"
                        title="What happened today"
                        description="Every step Dhruva took today, in order, with the reason it gave at the time."
                    >
                        {history.length ? <HistoryList items={history} /> : (
                            <Typography variant="body2" color="text.secondary">
                                {skipped ? decision.narrative : 'Nothing has happened yet today.'}
                            </Typography>
                        )}
                    </PanelFrame>
                </Box>
                <Box sx={{ gridColumn: { xs: 'auto', lg: 'span 12' } }}>
                    <Tooltip title={fetchedAt ? `Last refreshed ${formatTimestamp(fetchedAt)}` : ''}>
                        <Box>
                            <StrategyEventFeedPanel strategyLabel="Dhruva" eventFeedRows={events} feedHeight={320} />
                        </Box>
                    </Tooltip>
                </Box>
            </Box>
        </SectionFrame>
    );
};

export default DhruvaDesk;
